import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  capitalize,
  diffDaysFromISOString,
  formatCurrencyPortal,
  formatDateDigits,
  formatNumber
} from "../../../utils/FormatUtils";
import { DataType } from "../../../services/CompanyService";
import { AdvancedFiltersContext } from "../../shared/components/AdvancedFilters";
import PortalClientService, {
  PaymentLinkActions,
  PaymentProvider,
  PortalClientDataRequest,
  PortalClientResponse
} from "../../../services/PortalClientService";
import { SortDirection } from "../../shared/entities/Sort";
import { TranslationService } from "../../../services/TranslationService";
import Loading from "../../shared/components/Loading";
import ExportPortalService from "../../../services/ExportPortalService";
import PaginatorPortal from "../../shared/PaginatorPortal";
import TooltipComponent from "../../shared/TooltipComponent";
import { PaymentSummary } from "./PaymentSummary";
import { PaymentMethodModal } from "./PaymentMethodModal";
import { RedirectMessage } from "./RedirectMessage";
import { ToastService } from "../../shared/bootstrap/Toast";
import "./PortalInvoicesTable.css";
import "./PaymentSummary.css";

type HeaderType = {
  columnId: string;
  dataType: DataType;
  columnTitle: string;
};

const PORTAL_COLUMN_TRANSLATIONS: Partial<
  Record<string, keyof typeof TranslationService.translate>
> = {
  "-2": "IssueDate",
  "-3": "Amount",
  "-4": "Identifier",
  "-5": "DueDate",
  "-12": "PendingAmount",
  "-1011": "Amount",
  "-1012": "PendingAmount",
  "-1023": "IssueDate",
  "-1024": "DueDate",
  paidStatus: "Status"
};

type RowType = { Key: string; Value: number | string; dueDays?: any };

type SelectedInvoiceData = {
  id: number;
  nro: string;
  pendingAmount: number;
  currencySymbol: string;
  currencyId?: number;
  isEnabledToPay: boolean;
};

export const PortalInvoicesTable = ({
  response,
  selectedRows,
  setSelectedRows,
  selectAll,
  setSelectAll,
  setRequest,
  request,
  isLoading = true,
  selectedInvoices,
  setSelectedInvoices,
  tokenId,
  dataClient,
  allPaymentProviders
}: {
  response: any;
  selectedRows: number[];
  setSelectedRows: React.Dispatch<React.SetStateAction<number[]>>;
  selectAll: boolean;
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>;
  setRequest: (request: PortalClientDataRequest) => void;
  request: PortalClientDataRequest;
  isLoading: boolean;
  selectedInvoices: { id: number; nro: string }[];
  setSelectedInvoices: React.Dispatch<
    React.SetStateAction<{ id: number; nro: string }[]>
  >;
  tokenId: string;
  dataClient?: PortalClientResponse;
  allPaymentProviders?: PaymentProvider[];
}) => {
  const { headers = [], rowsToShow, currencyTotals } = response.data;
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentProviders, setPaymentProviders] = useState<PaymentProvider[]>(
    []
  );
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [redirectProviderName, setRedirectProviderName] = useState<string>("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Almacenar datos completos de facturas seleccionadas para persistir entre páginas
  const [selectedInvoicesData, setSelectedInvoicesData] = useState<
    Map<number, SelectedInvoiceData>
  >(new Map());

  // Feature flag para habilitar/deshabilitar funcionalidad de pago
  const paymentEnabled = dataClient?.allowPaymentProvider ?? false;

  // Verificar si se permite selección múltiple (viene en minúsculas desde el backend)
  const allowsMultipleInvoices = dataClient?.allowsMultipleInvoices ?? true;

  const managementEnabled = dataClient?.customerMessages ?? false;

  const tableHeaders: HeaderType[] = headers;
  const tableHeadersWithAction: HeaderType[] = useMemo(() => {
    const headerAction: HeaderType = {
      columnId: "download",
      columnTitle: TranslationService.translate.Download,
      dataType: DataType.Text
    };
    if (tableHeaders.length > 0) {
      const auxArr = [...tableHeaders, headerAction];
      return auxArr;
    } else {
      return [];
    }
  }, [tableHeaders]);

  const totalRows = Object.keys(currencyTotals);
  const advancedFiltersContext = useContext(AdvancedFiltersContext);
  const [sortOrder, setSortOrder] = useState<{
    [key: string]:
      | SortDirection.Descending
      | SortDirection.Ascending
      | undefined;
  }>({});

  // Calcular totales de facturas seleccionadas usando datos almacenados (persistente entre páginas)
  const paymentSummary = useMemo(() => {
    if (selectedRows.length === 0 || selectedInvoicesData.size === 0) {
      return {
        total: 0,
        currency: "",
        hasMultipleCurrencies: false,
        hasNonPayableInvoices: false,
        nonPayableInvoices: []
      };
    }

    let total = 0;
    let currency = "";
    const currencies = new Set<string>();
    const nonPayableInvoices: { id: number; nro: string }[] = [];

    // Iterar sobre todas las facturas seleccionadas almacenadas, no solo las de la página actual
    selectedInvoicesData.forEach((invoiceData, invoiceId) => {
      // Solo procesar facturas que están en selectedRows (por si se deseleccionaron)
      if (!selectedRows.includes(invoiceId)) {
        return;
      }

      // Verificar si la factura puede ser pagada
      if (!invoiceData.isEnabledToPay) {
        nonPayableInvoices.push({ id: invoiceData.id, nro: invoiceData.nro });
        return; // No incluir en el total si no es pagable
      }

      total += invoiceData.pendingAmount;
      if (invoiceData.currencySymbol) {
        currencies.add(invoiceData.currencySymbol);
      }

      if (!currency && invoiceData.currencySymbol) {
        currency = invoiceData.currencySymbol;
      }
    });

    return {
      total,
      currency,
      hasMultipleCurrencies: currencies.size > 1,
      hasNonPayableInvoices: nonPayableInvoices.length > 0,
      nonPayableInvoices
    };
  }, [selectedRows, selectedInvoicesData]);

  // Filtrar proveedores de pago según las monedas de las facturas seleccionadas
  const loadPaymentProviders = async (): Promise<PaymentProvider[] | null> => {
    // Usar los proveedores ya cargados desde el componente padre
    if (!allPaymentProviders || allPaymentProviders.length === 0) {
      return [];
    }

    // Si no hay facturas seleccionadas, retornar todos los proveedores
    if (selectedRows.length === 0 || selectedInvoicesData.size === 0) {
      setPaymentProviders(allPaymentProviders);
      return allPaymentProviders;
    }

    // Obtener los currencyIds únicos de las facturas seleccionadas
    const selectedCurrencyIds = new Set<number>();
    selectedInvoicesData.forEach((invoiceData, invoiceId) => {
      if (
        selectedRows.includes(invoiceId) &&
        invoiceData.currencyId !== undefined
      ) {
        selectedCurrencyIds.add(invoiceData.currencyId);
      }
    });

    // Si no hay currencyIds válidos, retornar todos los proveedores
    if (selectedCurrencyIds.size === 0) {
      setPaymentProviders(allPaymentProviders);
      return allPaymentProviders;
    }

    // Filtrar proveedores que tengan al menos una moneda compatible
    const filteredProviders = allPaymentProviders.filter((provider) => {
      if (!provider.Currencies || provider.Currencies.length === 0) {
        return false; // Si el proveedor no tiene monedas, no mostrarlo
      }
      // Verificar si el proveedor tiene alguna moneda que coincida con las facturas seleccionadas
      return provider.Currencies.some((currency) =>
        selectedCurrencyIds.has(currency.CurrencyID)
      );
    });

    setPaymentProviders(filteredProviders);
    return filteredProviders;
  };

  useEffect(() => {
    advancedFiltersContext.setShow(true);
  }, [advancedFiltersContext]);

  // Función para extraer datos de una factura desde una fila - memoizada
  const extractInvoiceData = useCallback(
    (
      row: any,
      dataClient?: PortalClientResponse
    ): SelectedInvoiceData | null => {
      const keyV = row.find((el: any) => el.Key === "ioid")?.Value;
      const fcV = row.find((el: any) => el.Key === "-4")?.Value;
      const isEnabledToPay = row.find(
        (el: any) => el.Key === "isEnableToPay"
      )?.Value;

      if (!keyV) return null;

      let pendingAmount = 0;
      let pendingColumnId: string | null = null;

      // Priorizar el campo configurado customersFieldAmountToPay si está disponible
      if (dataClient?.customersFieldAmountToPay) {
        pendingColumnId = dataClient.customersFieldAmountToPay.toString();
        const configuredRow = row.find((el: any) => el.Key === pendingColumnId);

        if (
          configuredRow?.Value !== undefined &&
          configuredRow?.Value !== null &&
          configuredRow?.Value !== ""
        ) {
          const parsed = parseFloat(configuredRow.Value);
          if (!isNaN(parsed)) {
            pendingAmount = parsed;
          }
        }
      }

      // Fallback: buscar el header del monto pendiente si no se encontró con el campo configurado
      if (pendingAmount === 0 && !pendingColumnId) {
        const pendingHeader = tableHeaders.find(
          (header: HeaderType) =>
            header.columnTitle.toLowerCase().includes("pendiente") ||
            header.columnId === "-12" ||
            header.columnId === "-1012"
        );
        pendingColumnId = pendingHeader?.columnId || "-12";

        // Buscar el monto pendiente
        const pendingRow = row.find(
          (el: any) =>
            el.Key === pendingColumnId || el.Key === "-12" || el.Key === "-1012"
        );

        if (
          pendingRow?.Value !== undefined &&
          pendingRow?.Value !== null &&
          pendingRow?.Value !== ""
        ) {
          const parsed = parseFloat(pendingRow.Value);
          if (!isNaN(parsed)) {
            pendingAmount = parsed;
          }
        }
      }

      const currencySymbol =
        row.find((el: any) => el.Key === "currencySymbol")?.Value || "";
      // Intentar extraer currencyId de la fila
      let currencyId: number | undefined = undefined;
      const currencyIdRow = row.find(
        (el: any) =>
          el.Key === "currencyId" ||
          el.Key === "currencyID" ||
          el.Key === "CurrencyID"
      );
      if (
        currencyIdRow?.Value !== undefined &&
        currencyIdRow?.Value !== null &&
        currencyIdRow?.Value !== ""
      ) {
        const parsed = parseInt(currencyIdRow.Value.toString());
        if (!isNaN(parsed)) {
          currencyId = parsed;
        }
      }

      return {
        id: keyV,
        nro: fcV || "",
        pendingAmount,
        currencySymbol,
        currencyId,
        isEnabledToPay: isEnabledToPay !== false && isEnabledToPay !== "false"
      };
    },
    [tableHeaders]
  );

  // Sincronizar estado selectAll cuando cambia la página
  useEffect(() => {
    const pageInvoiceIds = rowsToShow
      .map((row: any) => {
        const keyV = row.find((el: any) => el.Key === "ioid")?.Value;
        return keyV;
      })
      .filter((id: any) => id !== undefined);

    // Verificar si todas las facturas de la página actual están seleccionadas
    const allPageInvoicesSelected =
      pageInvoiceIds.length > 0 &&
      pageInvoiceIds.every((id: number) => selectedRows.includes(id));

    // Actualizar selectAll solo si difiere del estado actual
    if (selectAll !== allPageInvoicesSelected) {
      setSelectAll(allPageInvoicesSelected);
    }

    // Actualizar datos almacenados de facturas seleccionadas que están en esta página
    // (por si sus datos cambiaron al recargar)
    const invoicesToUpdate = pageInvoiceIds.filter((id: number) =>
      selectedRows.includes(id)
    );

    if (invoicesToUpdate.length > 0) {
      setSelectedInvoicesData((prev) => {
        const updated = new Map(prev);
        let hasChanges = false;

        invoicesToUpdate.forEach((invoiceId: number) => {
          const row = rowsToShow.find((row: any) => {
            const keyV = row.find((el: any) => el.Key === "ioid")?.Value;
            return keyV === invoiceId;
          });

          if (row) {
            const invoiceData = extractInvoiceData(row, dataClient);
            if (invoiceData) {
              const existingData = prev.get(invoiceId);
              // Solo actualizar si los datos cambiaron
              if (
                !existingData ||
                existingData.pendingAmount !== invoiceData.pendingAmount ||
                existingData.currencySymbol !== invoiceData.currencySymbol ||
                existingData.currencyId !== invoiceData.currencyId ||
                existingData.isEnabledToPay !== invoiceData.isEnabledToPay
              ) {
                updated.set(invoiceId, invoiceData);
                hasChanges = true;
              }
            }
          }
        });

        return hasChanges ? updated : prev;
      });
    }
  }, [
    rowsToShow,
    selectedRows,
    selectAll,
    extractInvoiceData,
    setSelectAll,
    dataClient
  ]);

  const toggleSortOrder = (columnId: string) => {
    let order: Partial<PortalClientDataRequest> = {};
    const newOrder =
      sortOrder[columnId] === SortDirection.Ascending
        ? SortDirection.Descending
        : sortOrder[columnId] === SortDirection.Descending
          ? undefined
          : SortDirection.Ascending;
    order.sortColumn =
      newOrder === undefined
        ? undefined
        : tableHeadersWithAction.find((h) => h.columnId === columnId)
            ?.columnTitle;
    order.descending = newOrder;
    setRequest({ ...request, ...order });
    setSortOrder({ [columnId]: newOrder });
  };

  const handleRowSelect = (id: number, nro: string) => {
    const isCurrentlySelected = selectedRows.includes(id);

    if (isCurrentlySelected) {
      // Deseleccionar: remover de todos los estados
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.filter((i) => i !== id)
      );
      setSelectedInvoices((prevSelectedRows) =>
        prevSelectedRows.filter((z) => z.id !== id)
      );
      setSelectedInvoicesData((prev) => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
      setSelectAll(false);
    } else {
      // Seleccionar: agregar a estados y extraer datos (siempre permitir múltiple selección)
      setSelectedRows((prevSelectedRows) => [...prevSelectedRows, id]);
      setSelectedInvoices((prevSelectedRows) => [
        ...prevSelectedRows,
        { id, nro }
      ]);

      // Buscar la fila correspondiente en rowsToShow para extraer sus datos
      const row = rowsToShow.find((row: any) => {
        const keyV = row.find((el: any) => el.Key === "ioid")?.Value;
        return keyV === id;
      });

      if (row) {
        const invoiceData = extractInvoiceData(row, dataClient);
        if (invoiceData) {
          setSelectedInvoicesData((prev) => {
            const newMap = new Map(prev);
            newMap.set(id, invoiceData);
            return newMap;
          });
        }
      }
    }
  };

  const handleSelectAll = () => {
    if (!selectAll) {
      // Seleccionar todas las facturas de la página actual
      const pageInvoiceIds: number[] = [];
      const pageInvoices: { id: number; nro: string }[] = [];
      const newInvoiceData = new Map<number, SelectedInvoiceData>();

      rowsToShow.forEach((row: any) => {
        const keyV = row.find((el: any) => el.Key === "ioid")?.Value;
        const fcV = row.find((el: any) => el.Key === "-4")?.Value;

        if (keyV) {
          pageInvoiceIds.push(keyV);
          pageInvoices.push({ id: keyV, nro: fcV || "" });

          // Extraer y almacenar datos de la factura
          const invoiceData = extractInvoiceData(row, dataClient);
          if (invoiceData) {
            newInvoiceData.set(keyV, invoiceData);
          }
        }
      });

      setSelectAll(true);
      // Combinar con facturas previamente seleccionadas de otras páginas
      setSelectedRows((prev) => [...new Set([...prev, ...pageInvoiceIds])]);
      setSelectedInvoices((prev) => {
        const existingIds = new Set(prev.map((inv) => inv.id));
        const newInvoices = pageInvoices.filter(
          (inv) => !existingIds.has(inv.id)
        );
        return [...prev, ...newInvoices];
      });

      // Agregar nuevos datos al mapa existente
      setSelectedInvoicesData((prev) => {
        const updated = new Map(prev);
        newInvoiceData.forEach((value, key) => {
          updated.set(key, value);
        });
        return updated;
      });
    } else {
      // Deseleccionar todas: solo remover las de la página actual
      const pageInvoiceIds = rowsToShow
        .map((row: any) => {
          const keyV = row.find((el: any) => el.Key === "ioid")?.Value;
          return keyV;
        })
        .filter((id: any) => id !== undefined);

      setSelectAll(false);
      setSelectedRows((prev) =>
        prev.filter((id) => !pageInvoiceIds.includes(id))
      );
      setSelectedInvoices((prev) =>
        prev.filter((inv) => !pageInvoiceIds.includes(inv.id))
      );

      // Remover datos de las facturas de esta página
      setSelectedInvoicesData((prev) => {
        const updated = new Map(prev);
        pageInvoiceIds.forEach((id: number) => {
          updated.delete(id);
        });
        return updated;
      });
    }
  };

  const handlePayClick = async () => {
    // Verificar si se permite pago múltiple y hay más de una factura seleccionada
    if (!allowsMultipleInvoices && selectedRows.length > 1) {
      return;
    }

    if (
      paymentSummary.hasMultipleCurrencies ||
      paymentSummary.hasNonPayableInvoices
    ) {
      return;
    }

    // Activar loader
    setIsProcessingPayment(true);

    // Cargar proveedores antes de abrir el modal
    const providers = await loadPaymentProviders();

    if (!providers || providers.length === 0) {
      setIsProcessingPayment(false);
      return;
    }

    // Verificar si hay un solo proveedor
    if (providers.length === 1) {
      const singleProvider = providers[0];

      // Si tiene redirección (TypeAction = 1), procesar automáticamente
      if (singleProvider.TypeAction === PaymentLinkActions.Redirect) {
        // El loader se mantendrá activo hasta que se abra el modal de redirección
        // Pasar el proveedor directamente para evitar problemas de sincronización del estado
        await handlePaymentMethodSelected(
          singleProvider.PaymentLinkProviderId.toString(),
          singleProvider
        );
        return; // No abrir el modal de selección
      } else if (singleProvider.TypeAction === PaymentLinkActions.RenderHtml) {
        // Si es RenderHtml, también procesar automáticamente
        await handlePaymentMethodSelected(
          singleProvider.PaymentLinkProviderId.toString(),
          singleProvider
        );
        return; // No abrir el modal de selección
      } else {
        // Si no tiene redirección o renderHtml, mostrar el modal normalmente
        setIsProcessingPayment(false);
        setIsPaymentModalOpen(true);
        return;
      }
    }

    // Si hay múltiples proveedores, mostrar el modal de selección
    setIsProcessingPayment(false);
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setPaymentProviders([]);
    setIsProcessingPayment(false);
  };

  const handleRedirectContinue = () => {
    if (redirectUrl) {
      sessionStorage.setItem("intiza_payment_return", "1");
      window.location.href = redirectUrl;
    }
  };

  const handleRedirectCancel = () => {
    setShowRedirectMessage(false);
    setRedirectUrl(null);
    setRedirectProviderName("");
  };

  const handleRenderHtmlPayment = (
    checkoutUrl: string,
    additionalData: string,
    providerName: string
  ) => {
    try {
      // Crear formulario HTML dinámico para hacer POST a ServiPag
      const form = document.createElement("form");
      form.method = "post";
      form.action = checkoutUrl;
      form.style.display = "none";

      // Crear input hidden con el XML
      const xmlInput = document.createElement("input");
      xmlInput.type = "hidden";
      xmlInput.name = "xml";
      xmlInput.value = additionalData;

      // Agregar input al formulario
      form.appendChild(xmlInput);

      // Agregar formulario al DOM
      document.body.appendChild(form);

      // Cerrar modal y desactivar loader antes de redirigir
      setIsPaymentModalOpen(false);
      setIsProcessingPayment(false);

      // Marcar que el usuario fue redirigido a un proveedor de pago
      sessionStorage.setItem("intiza_payment_return", "1");

      // Hacer submit del formulario (esto redirigirá al usuario)
      form.submit();

      // Remover el formulario del DOM después de un breve delay
      // (para asegurar que el submit se complete)
      setTimeout(() => {
        if (form.parentNode) {
          form.parentNode.removeChild(form);
        }
      }, 100);
    } catch (error) {
      console.error("Error al procesar pago RenderHtml:", error);
      ToastService.showToast(
        "Error al procesar el pago con ServiPag",
        undefined,
        "danger"
      );
      setIsPaymentModalOpen(false);
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentMethodSelected = async (
    providerId: string,
    provider?: PaymentProvider
  ) => {
    if (!tokenId || selectedRows.length === 0) {
      setIsProcessingPayment(false);
      return;
    }

    // Convertir providerId string a number
    const providerIdNumber = parseInt(providerId, 10);

    if (isNaN(providerIdNumber)) {
      ToastService.showToast("ID de proveedor inválido", undefined, "danger");
      setIsProcessingPayment(false);
      return;
    }

    // Obtener el proveedor seleccionado para verificar el tipo de acción
    // Si se pasa el proveedor directamente, usarlo; si no, buscarlo en el estado
    let selectedProvider = provider;
    if (!selectedProvider) {
      selectedProvider = paymentProviders.find(
        (p) => p.PaymentLinkProviderId === providerIdNumber
      );
    }

    if (!selectedProvider) {
      ToastService.showToast("Proveedor no encontrado", undefined, "danger");
      setIsProcessingPayment(false);
      return;
    }

    // Si TypeAction es NoSupport, no hacer nada
    if (selectedProvider.TypeAction === PaymentLinkActions.NoSupport) {
      ToastService.showToast(
        "Esta opción de pago no está disponible actualmente",
        undefined,
        "warning"
      );
      setIsPaymentModalOpen(false);
      setIsProcessingPayment(false);
      return;
    }

    // Obtener el currencyId de las facturas seleccionadas
    // Debe ser el mismo para todas (ya validado por hasMultipleCurrencies)
    let currencyId: number | undefined = undefined;
    if (selectedInvoicesData.size > 0) {
      const firstInvoice = Array.from(selectedInvoicesData.values()).find(
        (inv) => selectedRows.includes(inv.id)
      );
      if (firstInvoice?.currencyId !== undefined) {
        currencyId = firstInvoice.currencyId;
      }
    }

    // Llamar al endpoint sendPayment
    try {
      const result = await PortalClientService.sendPayment(
        {
          invoices: selectedRows,
          providerId: providerIdNumber,
          currencyId: currencyId
        },
        tokenId
      );

      if (result instanceof Error) {
        ToastService.showToast(
          TranslationService.translate.Network || "Error al procesar el pago",
          undefined,
          "danger"
        );
        setIsProcessingPayment(false);
        return;
      }

      // RequestPortalService devuelve la respuesta de axios, donde el body está en .data
      const response = result as any;
      const payment = response?.data?.payment || response?.payment;

      if (payment) {
        const typeAction = payment.TypeAction ?? PaymentLinkActions.Redirect; // Por compatibilidad, default a Redirect

        // Manejar según el tipo de acción
        if (typeAction === PaymentLinkActions.Redirect) {
          // TypeAction = 1 (Redirect): comportamiento original para OpenPay
          if (payment.CheckoutLink) {
            setRedirectUrl(payment.CheckoutLink);
            setRedirectProviderName(selectedProvider.PaymentProviderName);
            setIsPaymentModalOpen(false);
            // Desactivar loader cuando se muestra el modal de redirección
            setIsProcessingPayment(false);
            setShowRedirectMessage(true);
          } else {
            ToastService.showToast(
              "No se pudo obtener el link de pago",
              undefined,
              "danger"
            );
            setIsPaymentModalOpen(false);
            setIsProcessingPayment(false);
          }
        } else if (typeAction === PaymentLinkActions.RenderHtml) {
          // TypeAction = 2 (RenderHtml): POST con XML para ServiPag
          if (payment.CheckoutLink && payment.AdditionalData) {
            await handleRenderHtmlPayment(
              payment.CheckoutLink,
              payment.AdditionalData,
              selectedProvider.PaymentProviderName
            );
          } else {
            ToastService.showToast(
              "No se pudo obtener la información de pago",
              undefined,
              "danger"
            );
            setIsPaymentModalOpen(false);
            setIsProcessingPayment(false);
          }
        } else {
          // TypeAction = 0 (NoSupport) o desconocido
          ToastService.showToast(
            "Esta opción de pago no está disponible actualmente",
            undefined,
            "warning"
          );
          setIsPaymentModalOpen(false);
          setIsProcessingPayment(false);
        }
      } else {
        ToastService.showToast(
          "No se pudo procesar el pago",
          undefined,
          "danger"
        );
        setIsPaymentModalOpen(false);
        setIsProcessingPayment(false);
      }
    } catch (error) {
      console.error("Error al procesar pago:", error);
      ToastService.showToast("Error al procesar el pago", undefined, "danger");
      setIsProcessingPayment(false);
    }
  };

  return (
    <>
      {isProcessingPayment && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-container">
            <Loading />
            <p className="mt-3">Procesando pago...</p>
          </div>
        </div>
      )}

      {(paymentEnabled || managementEnabled) && (
        <PaymentSummary
          selectedCount={selectedRows.length}
          totalAmount={paymentSummary.total}
          currencySymbol={paymentSummary.currency}
          onPayClick={handlePayClick}
          showCents={dataClient?.showCents || false}
          hasMultipleCurrencies={paymentSummary.hasMultipleCurrencies}
          canPay={paymentEnabled}
          canManage={managementEnabled}
          hasNonPayableInvoices={paymentSummary.hasNonPayableInvoices}
          nonPayableInvoices={paymentSummary.nonPayableInvoices}
          allowsMultipleInvoices={allowsMultipleInvoices}
        />
      )}

      <div
        className={`portal-invoices-container ${!paymentEnabled ? "no-payment" : ""}`}
      >
        <div className="portal-table-card">
          {Boolean(response.data.count) ? (
            <>
              <div className="portal-table-wrapper">
                <table className="portal-invoices-table">
                  <thead>
                    <tr>
                      <th className="portal-checkbox-column">
                        <input
                          type="checkbox"
                          className="portal-checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </th>
                      {tableHeadersWithAction?.map((header, i) => (
                        <HeaderValue
                          key={i}
                          header={header}
                          sortOrder={sortOrder[header.columnId]}
                          onClick={() => toggleSortOrder(header.columnId)}
                        />
                      ))}
                    </tr>
                  </thead>

                  {!isLoading && (
                    <tbody>
                      {rowsToShow.map((row: any, i: number) => {
                        const fcV = row.find(
                          (el: any) => el.Key === "-4"
                        )?.Value;
                        const keyV = row.find(
                          (el: any) => el.Key === "ioid"
                        )?.Value;
                        const hasFiles = row.find(
                          (el: any) => el.Key === "hasFiles"
                        )?.Value;

                        return (
                          <tr key={i}>
                            <td className="portal-checkbox-column">
                              <input
                                type="checkbox"
                                className="portal-checkbox"
                                checked={selectedRows.includes(keyV)}
                                onChange={() => handleRowSelect(keyV, fcV)}
                              />
                            </td>
                            {tableHeadersWithAction?.map((header, id) => {
                              const rowToShow: any =
                                header.columnId === "download"
                                  ? { Key: "download", Value: keyV }
                                  : row.find(
                                      (r: any) => r.Key === header.columnId
                                    );
                              const currency =
                                header.dataType === DataType.Currency
                                  ? row
                                      .find(
                                        (r: any) => r.Key === "currencySymbol"
                                      )
                                      ?.Value.toString()
                                  : "";
                              return rowToShow ? (
                                <ItemValue
                                  key={id}
                                  row={rowToShow}
                                  header={header}
                                  currency={currency || ""}
                                  hasFiles={hasFiles}
                                  tokenId={tokenId}
                                  showCents={dataClient.showCents}
                                />
                              ) : null;
                            })}
                          </tr>
                        );
                      })}
                      {totalRows?.map((ct, k) => {
                        const dataCurrency = currencyTotals[ct];
                        const simbol = ct?.split(", ")[1].replace(")", "");
                        return (
                          <tr key={k} className="portal-totals-row">
                            <td className="portal-checkbox-column"></td>
                            {tableHeadersWithAction?.map((header, i) => {
                              const hideSymbol =
                                header.dataType !== DataType.Currency;
                              const rowToShow = dataCurrency.find(
                                (r: any) =>
                                  r.Item1.toString() ===
                                  header.columnId.toString()
                              );
                              return (
                                <td key={i}>
                                  <div className="portal-text-end">
                                    {rowToShow?.Item2 >= 0
                                      ? hideSymbol
                                        ? formatNumber(
                                            parseFloat(rowToShow.Item2)
                                          )
                                        : formatCurrencyPortal(
                                            rowToShow.Item2 || 0,
                                            simbol,
                                            dataClient.showCents
                                          )
                                      : ""}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  )}
                </table>
                {isLoading && (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ backgroundColor: "#f2f1f6", minHeight: "300px" }}
                  >
                    <Loading />
                  </div>
                )}
              </div>
              <div className="portal-paginator-fixed">
                <PaginatorPortal />
              </div>
            </>
          ) : (
            <div className="portal-no-data">
              {TranslationService.translate.NoDataToShow}
            </div>
          )}
        </div>
      </div>

      {paymentEnabled && (
        <PaymentMethodModal
          isOpen={isPaymentModalOpen}
          onClose={handleClosePaymentModal}
          onNext={handlePaymentMethodSelected}
          providers={paymentProviders.map((p) => ({
            id: p.PaymentLinkProviderId.toString(),
            name: p.PaymentProviderName,
            logo: p.UrlLogo
          }))}
          loading={false}
          totalAmount={paymentSummary.total}
          currencySymbol={paymentSummary.currency}
          selectedCount={selectedRows.length}
          showCents={dataClient?.showCents || false}
          hasMultipleCurrencies={paymentSummary.hasMultipleCurrencies}
        />
      )}

      {showRedirectMessage && (
        <RedirectMessage
          providerName={redirectProviderName}
          onContinue={handleRedirectContinue}
          onCancel={handleRedirectCancel}
        />
      )}
    </>
  );
};

const HeaderValue = ({
  header,
  sortOrder,
  onClick
}: {
  header: HeaderType;
  sortOrder: SortDirection.Ascending | SortDirection.Descending | undefined;
  onClick: () => void;
}) => {
  const alignClass = getAlignClass(header);
  const translationKey = PORTAL_COLUMN_TRANSLATIONS[header.columnId];
  const headerTitle = capitalize(
    translationKey
      ? TranslationService.translate[translationKey]
      : header.columnTitle
  );
  const isDueDate = header.columnId === "-5" || header.columnId === "-1024";
  const dueDateClass = isDueDate ? "portal-due-date-column" : "";
  const isCurrency = header.dataType === DataType.Currency;
  const isNumber = header.dataType === DataType.Number;
  const isList = header.dataType === DataType.List;
  const isDownload = header.columnId === "download";

  // Determinar la clase de justificación para el div interno
  let justifyClass = "";
  if (isNumber || isCurrency || isDueDate) {
    justifyClass = "justify-content-end";
  } else if (isDownload || isList) {
    justifyClass = "justify-content-center";
  } else {
    justifyClass = "justify-content-start";
  }

  return (
    <th
      onClick={onClick}
      className={`portal-sortable-header ${alignClass} ${dueDateClass}`}
    >
      <TooltipComponent title={headerTitle}>
        <div className={`d-flex align-items-center ${justifyClass}`}>
          <span className="portal-header-text">{headerTitle}</span>
          {sortOrder !== undefined && (
            <span className="portal-sort-icon">
              {sortOrder === SortDirection.Ascending ? "▲" : "▼"}
            </span>
          )}
        </div>
      </TooltipComponent>
    </th>
  );
};

const getAlignClass = (header: HeaderType): string => {
  const isCurrency = header.dataType === DataType.Currency;
  const isNumber = header.dataType === DataType.Number;
  const isList = header.dataType === DataType.List;
  const isDueDate = header.columnId === "-5" || header.columnId === "-1024";
  const isDownload = header.columnId === "download";

  if (isNumber || isCurrency || isDueDate) {
    return "portal-text-end justify-content-end";
  } else if (isDownload || isList) {
    return "portal-text-center justify-content-center";
  } else {
    return "portal-text-start justify-content-start";
  }
};

const alignByType = (header: HeaderType): string => {
  const isCurrency = header.dataType === DataType.Currency;
  const isNumber = header.dataType === DataType.Number;
  const isList = header.dataType === DataType.List;
  const isDueDate = header.columnId === "-5" || header.columnId === "-1024";
  const isDownload = header.columnId === "download";
  return isNumber || isCurrency || isDueDate
    ? "text-end  align-items-end justify-content-end"
    : isDownload || isList
      ? "text-center  align-items-center justify-content-center"
      : "text-start  align-items-start justify-content-start";
};

const ItemValue = ({
  row,
  header,
  currency = "",
  hasFiles = false,
  tokenId,
  showCents = false
}: {
  row: RowType;
  header: HeaderType;
  currency: string;
  hasFiles: boolean;
  tokenId: string;
  showCents: boolean;
}) => {
  let endClass = alignByType(header);
  const valueRow = row?.Value ? row.Value.toString() : "";
  let dataRow: any = valueRow;

  const dueDaysCounter =
    row.Key === "-5"
      ? row?.dueDays || row?.Value
        ? diffDaysFromISOString(row.Value?.toString())
        : 0
      : 0;
  const isDueValue = dataRow && row?.dueDays && row?.dueDays?.Value > 0;
  const isDownload = header.columnId === "download";
  const isDueDateColumn =
    header.columnId === "-5" || header.columnId === "-1024";

  if (isDueDateColumn) {
    endClass += " portal-due-date-column";
  }

  const downloadFile = async () => {
    await ExportPortalService.requestExport(
      "/customer/downloadiofiles",
      {
        ioID: row.Value.toString()
      },
      1,
      "customer_list.xlsx",
      tokenId
    );
  };

  if (header.dataType === DataType.Number) {
    dataRow = valueRow ? formatNumber(parseFloat(valueRow)) : "";
  } else if (header.dataType === DataType.Currency && currency) {
    dataRow = formatCurrencyPortal(
      parseFloat(valueRow || "0"),
      currency,
      showCents
    );
  } else if (header.dataType === DataType.Date) {
    dataRow = valueRow ? formatDateDigits(new Date(valueRow)) : "";
  } else if (header.dataType === DataType.Link && valueRow.length > 0) {
    dataRow = (
      <a href={valueRow} target="_blank noreferrer">
        Link
      </a>
    );
  } else if (row.Key === "paidStatus") {
    switch (valueRow) {
      case "0":
      case "":
        dataRow = "Pendiente";
        break;
      case "1":
        dataRow = "Completado";
        break;
      case "2":
        dataRow = "Vencido";
        if (dueDaysCounter < 0) {
          endClass = " dueStatus";
        }
        break;
      default:
        dataRow = valueRow;
        break;
    }
  }

  return (
    <td className={endClass}>
      {dueDaysCounter < 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2
          }}
        >
          <span className="portal-due-date-pill">{dataRow}</span>
          {isDueValue ? (
            <span className="portal-days-overdue">
              ({Math.abs(dueDaysCounter)} {TranslationService.translate.Days})
            </span>
          ) : null}
        </div>
      ) : isDownload ? (
        <div className="portal-text-center">
          <span onClick={downloadFile}>
            {hasFiles ? (
              <i className="fa fa-download portal-download-icon" />
            ) : (
              ""
            )}
          </span>
        </div>
      ) : (
        dataRow
      )}
    </td>
  );
};
